// Scene editor state: immutable SceneDesc snapshots with undo/redo.

import { emptyScene, type SceneDesc, type SceneNode } from './types';

const KEY = 'stereo.scene.v1';
const MAX_UNDO = 100;

function load(): SceneDesc {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as SceneDesc;
  } catch {
    /* ignore */
  }
  return starterScene();
}

/** A small default scene so the editor is not empty. */
export function starterScene(): SceneDesc {
  const s = emptyScene();
  s.nodes.push(
    {
      id: 'torus1',
      name: 'Torus',
      parent: null,
      transform: { position: [0, 0, 0], rotation: [20, 0, 0], scale: [1, 1, 1] },
      visible: true,
      shape: { kind: 'torus', radius: 1.2, tube: 0.45, radial_segments: 24, tubular_segments: 72 },
    },
    {
      id: 'ball1',
      name: 'Sphere',
      parent: null,
      transform: { position: [0, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1] },
      visible: true,
      shape: { kind: 'sphere', radius: 0.55, width_segments: 48, height_segments: 24 },
    },
  );
  s.timeline = {
    duration: 4,
    tracks: [
      {
        target: 'torus1',
        keys: [
          { time: 0, rotation: [20, 0, 0], easing: 'linear' },
          { time: 4, rotation: [20, 360, 0], easing: 'linear' },
        ],
      },
    ],
  };
  return s;
}

class SceneStore {
  current = $state.raw<SceneDesc>(load());
  selected = $state<string | null>(null);
  time = $state(0);
  private undoStack: SceneDesc[] = [];
  private redoStack: SceneDesc[] = [];
  revision = $state(0);

  /** Replaces the scene, recording the previous state for undo. */
  commit(next: SceneDesc) {
    this.undoStack.push(this.current);
    if (this.undoStack.length > MAX_UNDO) this.undoStack.shift();
    this.redoStack = [];
    this.current = next;
    this.revision++;
    this.save();
  }

  /** Applies a change to a copy of the scene. */
  update(fn: (draft: SceneDesc) => void) {
    const next = structuredClone(this.current);
    fn(next);
    this.commit(next);
  }

  updateNode(id: string, fn: (n: SceneNode) => void) {
    this.update((d) => {
      const n = d.nodes.find((x) => x.id === id);
      if (n) fn(n);
    });
  }

  get canUndo() {
    void this.revision;
    return this.undoStack.length > 0;
  }

  get canRedo() {
    void this.revision;
    return this.redoStack.length > 0;
  }

  undo() {
    const prev = this.undoStack.pop();
    if (!prev) return;
    this.redoStack.push(this.current);
    this.current = prev;
    this.revision++;
    this.save();
  }

  redo() {
    const next = this.redoStack.pop();
    if (!next) return;
    this.undoStack.push(this.current);
    this.current = next;
    this.revision++;
    this.save();
  }

  replace(scene: SceneDesc) {
    this.commit(scene);
    this.selected = null;
  }

  private saveTimer: ReturnType<typeof setTimeout> | undefined;
  private save() {
    clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      try {
        localStorage.setItem(KEY, JSON.stringify(this.current));
      } catch {
        /* quota (large meshes): the scene can still be saved to a file */
      }
    }, 400);
  }
}

export const sceneStore = new SceneStore();
